// src/components/tour/ForumTour.jsx
import { useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const ForumTour = ({ user }) => {
  const tourRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const handleStart = () => {
      if (tourRef.current) {
        try {
          tourRef.current.complete();
          tourRef.current.cancel();
        } catch (e) {
          console.log('Tour cleanup error:', e);
        }
        tourRef.current = null;
      }

      document.querySelectorAll('.shepherd-element, .shepherd-modal-overlay-container').forEach(el => el.remove());

      const stepKey = localStorage.getItem('tour_step');
      const isMobile = window.innerWidth <= 768;

      const createTour = (steps) => {
        disableBodyScroll(document.body);

        tourRef.current = new Shepherd.Tour({
          defaultStepOptions: {
            cancelIcon: { enabled: true },
            classes: 'shadow-md bg-[#1a1a1a] text-white rounded-xl',
            scrollTo: { behavior: 'smooth', block: 'center' }
          },
          useModalOverlay: true
        });

        tourRef.current.on('show', (step) => {
          document.querySelectorAll('.shepherd-target-highlight')?.forEach(el => {
            el.classList.remove('shepherd-target-highlight');
          });
          const target = step?.options?.attachTo?.element;
          if (typeof target === 'string') {
            const el = document.querySelector(target);
            el?.classList?.add('shepherd-target-highlight');
          }
        });

        tourRef.current.on('hide', () => {
          document.querySelectorAll('.shepherd-target-highlight')?.forEach(el => el.classList.remove('shepherd-target-highlight'));
        });

        tourRef.current.on('complete', () => {
          clearAllBodyScrollLocks();
          tourRef.current = null;
        });

        tourRef.current.on('cancel', () => {
          clearAllBodyScrollLocks();
          tourRef.current = null;
        });

        steps.forEach((step, index) => {
          const total = steps.length;
          tourRef.current.addStep({
            ...step,
            text: () => {
              const progressDots = Array.from({ length: total }).map((_, i) => {
                if (i < index) return '<span class="tour-dot tour-dot-completed"></span>';
                if (i === index) return '<span class="tour-dot tour-dot-current"></span>';
                return '<span class="tour-dot"></span>';
              }).join('');
              return `
                <div class="tour-text-content">
                  ${step.text}
                  <div class="tour-step-footer">
                    <div class="tour-progress">Step ${index + 1} of ${total}</div>
                    <div class="tour-dots">${progressDots}</div>
                  </div>
                </div>
              `;
            }
          });
        });

        tourRef.current.start();
      };

      const forumSteps = [
        {
          id: 'search-posts',
          title: 'Search Posts',
          text: 'Quickly find relevant posts by entering keywords, tags, or author names.',
          attachTo: { element: 'input[placeholder="Search posts..."]', on: 'bottom' },
          buttons: [{ text: 'Next', action: () => tourRef.current.next() }]
        },
        {
          id: 'sort-posts',
          title: 'Sort Posts',
          text: 'Organize posts by the latest entries or those with the most upvotes.',
          attachTo: { element: 'select', on: 'bottom' },
          buttons: [
            { text: 'Back', action: () => tourRef.current.back() },
            { text: 'Next', action: () => tourRef.current.next() }
          ]
        },
        {
          id: 'create-post',
          title: 'Create Post',
          text: 'Start a new discussion or share your insights by creating a post.',
          attachTo: { element: 'a[href="/create-post"]', on: 'bottom' },
          buttons: [{ text: 'Next', action: () => tourRef.current.next() }]
        },
        {
          id: 'post-detail-video',
          title: 'Annotate & Discuss',
          text: `
            <div class="tour-video-content">
              <p>Here's how to use the Post Detail page with annotations and discussions:</p>
              <video 
                controls 
                autoplay 
                muted 
                loop
                style="width: 100%; max-width: 350px; border-radius: 8px; margin: 10px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.3);"
              >
                <source src="/videos/annotation-demo-final.mp4" type="video/mp4">
                Your browser does not support the video tag.
              </video>
              <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 0.8rem; color: #94a3b8;">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                Click on any post to experience these interactive features!
              </div>
            </div>
          `,
          attachTo: { element: '.post-card', on: 'top' },
          buttons: [{
            text: 'Finish Tour ✨',
            action: () => {
              localStorage.removeItem('tour_step');
              clearAllBodyScrollLocks();
              if (tourRef.current) {
                tourRef.current.complete();
                tourRef.current = null;
              }
            }
          }]
        }
      ];

      if (stepKey === 'sidebar') {
        if (isMobile) {
          createTour([
            {
              id: 'mobile-sidebar',
              title: 'Sidebar Menu',
              text: 'Tap this icon to open the sidebar and explore forum sections.',
              attachTo: { element: '.hamburger-icon', on: 'right' },
              buttons: [
                {
                  text: 'Next',
                  action: () => {
                    tourRef.current.complete();
                    tourRef.current = null;
                    localStorage.setItem('tour_step', 'forum');
                    setTimeout(() => window.dispatchEvent(new Event('start-tour')), 200);
                  }
                }
              ]
            }
          ]);
        } else {
          // Desktop sidebar full tour
          createTour([
            {
              id: 'sidebar-home',
              title: 'Forum',
              text: 'Navigate back to the main forum where all discussions and posts are listed.',
              attachTo: { element: 'a[href="/forum"]', on: 'right' },
              buttons: [{ text: 'Next', action: () => tourRef.current.next() }]
            },
            {
              id: 'sidebar-blogs',
              title: 'Blogs',
              text: 'Explore in-depth articles and insights shared by community members.',
              attachTo: { element: 'a[href="/blog"]', on: 'right' },
              buttons: [
                { text: 'Back', action: () => tourRef.current.back() },
                { text: 'Next', action: () => tourRef.current.next() }
              ]
            },
            {
              id: 'sidebar-about',
              title: 'About',
              text: 'Learn more about this platform, its mission, and how it works.',
              attachTo: { element: 'a[href="/about"]', on: 'right' },
              buttons: [
                { text: 'Back', action: () => tourRef.current.back() },
                { text: 'Next', action: () => tourRef.current.next() }
              ]
            },
            {
              id: 'sidebar-profile',
              title: 'Profile',
              text: 'Access your profile settings or log out from your account here.',
              attachTo: { element: '.profile-menu-container', on: 'right' },
              buttons: [{
                text: 'Next',
                action: () => {
                  tourRef.current.complete();
                  tourRef.current = null;
                  setTimeout(() => {
                    localStorage.setItem('tour_step', 'forum');
                    window.dispatchEvent(new Event('start-tour'));
                  }, 200);
                }
              }]
            }
          ]);
        }
      }

      if (stepKey === 'forum') {
        createTour(forumSteps);
      }
    };

    window.addEventListener('start-tour', handleStart);

    return () => {
      window.removeEventListener('start-tour', handleStart);
      clearAllBodyScrollLocks();
      document.querySelectorAll('.shepherd-target-highlight')?.forEach(el => el.classList.remove('shepherd-target-highlight'));
      if (tourRef.current) {
        try {
          tourRef.current.complete();
          tourRef.current.cancel();
        } catch (e) {
          console.log('Cleanup error:', e);
        }
        tourRef.current = null;
      }
      document.querySelectorAll('.shepherd-element, .shepherd-modal-overlay-container').forEach(el => el.remove());
    };
  }, [user]);

  return null;
};

export default ForumTour;
